import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import axios from '../utils/axiosConfig';

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  descripcion?: string;
  anio: number;
  cuatrimestre: number;
  tipoEvaluacion: 'nota_completa' | 'nota_por_tema';
  cantidadTemas: number;
  activo: boolean;
  profesorTitular?: {
    id: number;
    nombre: string;
    apellido: string;
  };
  turnos?: any[];
  docentes?: any[];
}

interface SelectedCourseContextType {
  selectedCourse: Curso | null;
  setSelectedCourse: (course: Curso | null) => void;
  courses: Curso[];
  loading: boolean;
  refreshCourses: () => void;
}

const SelectedCourseContext = createContext<SelectedCourseContextType | undefined>(undefined);

export const useSelectedCourse = () => {
  const context = useContext(SelectedCourseContext);
  if (!context) {
    throw new Error('useSelectedCourse must be used within a SelectedCourseProvider');
  }
  return context;
};

interface SelectedCourseProviderProps {
  children: ReactNode;
}

export const SelectedCourseProvider: React.FC<SelectedCourseProviderProps> = ({ children }) => {
  const [selectedCourse, setSelectedCourse] = useState<Curso | null>(null);
  const [courses, setCourses] = useState<Curso[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchCourses = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get('/cursos');
      const fetchedCourses = Array.isArray(response.data) ? response.data : [];
      setCourses(fetchedCourses);

      // Si solo hay un curso, seleccionarlo automáticamente
      if (fetchedCourses.length === 1) {
        setSelectedCourse(fetchedCourses[0]);
      } else if (fetchedCourses.length > 1) {
        // Si hay múltiples cursos, intentar recuperar el último seleccionado del localStorage
        const lastSelectedCourseId = localStorage.getItem('lastSelectedCourseId');
        if (lastSelectedCourseId) {
          const lastCourse = fetchedCourses.find((c: Curso) => c.id === parseInt(lastSelectedCourseId));
          if (lastCourse) {
            setSelectedCourse(lastCourse);
          } else {
            // Si el curso guardado no existe más, seleccionar el primero
            setSelectedCourse(fetchedCourses[0]);
          }
        } else {
          // Si no hay curso guardado, seleccionar el primero por defecto
          setSelectedCourse(fetchedCourses[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Guardar el curso seleccionado en localStorage cuando cambia
  useEffect(() => {
    if (selectedCourse) {
      localStorage.setItem('lastSelectedCourseId', selectedCourse.id.toString());
    }
  }, [selectedCourse]);

  const handleSetSelectedCourse = (course: Curso | null) => {
    setSelectedCourse(course);
    if (course) {
      localStorage.setItem('lastSelectedCourseId', course.id.toString());
    } else {
      localStorage.removeItem('lastSelectedCourseId');
    }
  };

  return (
    <SelectedCourseContext.Provider
      value={{
        selectedCourse,
        setSelectedCourse: handleSetSelectedCourse,
        courses,
        loading,
        refreshCourses: fetchCourses,
      }}
    >
      {children}
    </SelectedCourseContext.Provider>
  );
};

